// Pagina HTML a Bonului de consum (formular tipizat 14-3-4A) pentru o iesire de material
// catre productie. Primeste datele de la /api/miscari/{id}/bon-consum si intoarce un
// document HTML complet, A4 landscape, cu o bara de sus (Print + Inchide) care NU apare
// la tiparire. Se deschide intr-o fereastra noua; tiparirea se face abia cand
// utilizatorul apasa Print pe acea pagina. Nu se mai genereaza PDF.

import { formatCantitate } from './format';
import { formatNumarRaport } from './export';

function esc(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// "2026-02-28" -> "28.02.2026" (fara obiect Date, ca sa nu apara decalaje de fus orar)
function dataRo(iso) {
  if (!iso) return '-';
  const [an, luna, zi] = String(iso).slice(0, 10).split('-');
  return `${zi}.${luna}.${an}`;
}

const gol = v => v == null || String(v).trim() === '';
const text = v => (gol(v) ? '-' : String(v));
const codNume = (cod, nume) => {
  const parti = [cod, nume].filter(v => !gol(v));
  return parti.length ? parti.join(' - ') : '-';
};
const nr = (v, formateaza) => (v == null ? '-' : formateaza(v));

// Latimile coloanelor tabelului (proportii), ca pe formularul original:
// Nr / Denumire / Cont debitor / Cont creditor / Cant. necesara / Cod / UM / Cant. eliberata / Pret / Valoare
const COLOANE = [30, 150, 55, 55, 65, 70, 40, 65, 70, 70];
const SUMA = COLOANE.reduce((a, b) => a + b, 0);
const colgroup = `<colgroup>${COLOANE.map(w => `<col style="width:${(w * 100 / SUMA).toFixed(2)}%">`).join('')}</colgroup>`;

const STIL = `
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #000; margin: 0; background: #eef0f4; }
  .bara { position: sticky; top: 0; z-index: 5; background: #16213e; color: #fff; padding: 10px 18px;
          display: flex; align-items: center; gap: 12px; font-size: 13px; }
  .bara .spatiu { flex: 1; }
  .bara button { border: none; border-radius: 6px; padding: 8px 18px; font-size: 14px; font-weight: 600; cursor: pointer; }
  .bara .print { background: #27ae60; color: #fff; }
  .bara .inchide { background: #f1f3f8; color: #3a4257; }
  .pagina { background: #fff; width: 297mm; margin: 14px auto; padding: 10mm; box-shadow: 0 2px 10px rgba(0,0,0,.15); }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table + table { margin-top: -0.6pt; }
  td, th { border: 0.6pt solid #000; padding: 3px 5px; vertical-align: middle; word-wrap: break-word; }
  .antet td { height: 34pt; font-size: 11px; line-height: 1.5; }
  .antet td.titlu { text-align: center; font-size: 17px; font-weight: 700; }
  .info td { height: 38pt; vertical-align: top; padding-top: 4px; }
  .info .eticheta { display: block; font-size: 9px; margin-bottom: 8px; }
  .info .valoare { display: block; font-size: 11px; font-weight: 700; }
  .tabel th { height: 26pt; font-size: 10.5px; font-weight: 700; text-align: center; }
  .tabel td { height: 26pt; font-size: 11px; text-align: center; }
  .tabel td.stanga { text-align: left; }
  .tabel td.total-eticheta { height: 18pt; text-align: right; font-weight: 700; font-size: 12px; padding-right: 8px; }
  .tabel td.total-valoare { height: 18pt; font-weight: 700; }
  .semn td { height: 16pt; font-size: 11px; }
  .semn tr.gol td { height: 30pt; }
  @media print {
    body { background: #fff; }
    .bara { display: none; }
    .pagina { width: auto; margin: 0; padding: 0; box-shadow: none; }
  }
`;

// b = raspunsul API (vezi BonConsumController.construiesteBon)
export function construiesteHtmlBonConsum(b) {
  const numarDocument = gol(b.document) ? `MS-${b.id}` : String(b.document);
  const comanda = b.comandaId != null
    ? `#${b.comandaId}${gol(b.client) ? '' : ' - ' + b.client}`
    : '-';

  const casete = [
    ['Număr document', numarDocument],
    ['Data', dataRo(b.dataDocument)],
    ['Cod predător', codNume(b.magazieSursaCod, b.magazieSursaNume)],
    ['Cod primitor', codNume(b.magaziePrimitorCod, b.magaziePrimitorNume)],
    ['Nr. comandă', b.comandaId != null ? `#${b.comandaId}` : '-'],
    ['Cod produs', codNume(b.produsCod, b.produsDenumire)]
  ];

  const cantitate = nr(b.cantitate, formatCantitate);
  const valoare = nr(b.valoareTotala, formatNumarRaport);

  return `<!DOCTYPE html><html lang="ro"><head><meta charset="utf-8">
    <title>${esc('Bon de consum ' + numarDocument)}</title><style>${STIL}</style></head><body>
    <div class="bara">
      <span>Previzualizare bon de consum &middot; A4 landscape</span>
      <span class="spatiu"></span>
      <button type="button" class="print" onclick="window.print()">Print</button>
      <button type="button" class="inchide" onclick="window.close()">Inchide</button>
    </div>
    <div class="pagina">
      <table class="antet"><colgroup><col style="width:42%"><col style="width:28%"><col style="width:30%"></colgroup>
        <tr>
          <td>Compania: ___________________________<br>Unitatea: ___________________________</td>
          <td>Produs, lucrare (comandă):<br>${esc(comanda)}</td>
          <td class="titlu">BON DE CONSUM</td>
        </tr>
      </table>

      <table class="info">
        <tr>${casete.map(([eticheta, valoareCasuta]) =>
          `<td><span class="eticheta">${esc(eticheta)}</span><span class="valoare">${esc(valoareCasuta)}</span></td>`
        ).join('')}</tr>
      </table>

      <table class="tabel">
        ${colgroup}
        <thead><tr>
          <th>Nr.<br>crt</th><th>Denumirea materialelor</th><th>Cont<br>Debitor</th><th>Cont<br>Creditor</th>
          <th>Cantitatea<br>necesară</th><th>Cod</th><th>U/M</th><th>Cantitate<br>eliberată</th>
          <th>Preț<br>unitar</th><th>Valoare</th>
        </tr></thead>
        <tbody>
          <tr>
            <td>1</td>
            <td class="stanga">${esc(codNume(b.materialCod, b.materialDenumire))}</td>
            <td></td><td></td>
            <td>${esc(cantitate)}</td>
            <td>${esc(text(b.materialCod))}</td>
            <td>${esc(text(b.um))}</td>
            <td>${esc(cantitate)}</td>
            <td>${esc(nr(b.pretUnitar, formatNumarRaport))}</td>
            <td>${esc(valoare)}</td>
          </tr>
          <tr>
            <td class="total-eticheta" colspan="9">Total:</td>
            <td class="total-valoare">${esc(valoare)}</td>
          </tr>
        </tbody>
      </table>

      <table class="semn"><colgroup><col style="width:80pt"><col><col><col></colgroup>
        <tr><td>Data și<br>semnătura</td><td>Șef compartiment</td><td>Gestionar</td><td>Primitor</td></tr>
        <tr class="gol"><td></td><td></td><td></td><td></td></tr>
      </table>
    </div></body></html>`;
}
